import {
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import HeadingWithDescription from '../../../components/headingWithDescription';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { MachineDetails } from '../../../types/manageAccount.type';
import ManageAccountService from '../../../services/manageAccount.service';
import ActivityIndicator from '../../../components/activityIndicator';
import { getProductImageByName } from '../../../helpers/imageHelpers';
import { getFormattedDate } from '../../../helpers/serviceHelpers';
import { CalendarIcon } from '@mui/x-date-pickers';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { OUT_OF_WARRANTY } from '../../../constants/longStrings.constants';

function SingleRow({ label, value }: { label: string; value: string }) {
  return (
    <TableRow>
      <TableCell
        sx={{
          px: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <CalendarIcon sx={{ fontSize: '14px' }} /> <Typography variant="body2">{label}</Typography>
      </TableCell>
      <TableCell align="right">{value}</TableCell>
    </TableRow>
  );
}

export default function Machines() {
  const userId = useSelector((state: RootState) => state.auth.userId);
  const token = useSelector((state: RootState) => state.auth.token);
  const [loading, setLoading] = useState(true);

  const [machines, setMachines] = useState<MachineDetails[]>([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const getMachinesDetails = async () => {
    if (!userId || !token) return;
    try {
      setLoading(true);
      const res = await ManageAccountService.getAccountInfoAsync(userId, token);
      const data = res?.machine_details;
      if (data) {
        setMachines([...data]);
      }
    } catch (error) {
      console.log('Error in getMachinesDetails', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    userId && getMachinesDetails();
    // eslint-disable-next-line
  }, [userId]);
  return (
    <>
      {loading && <ActivityIndicator />}
      <Stack p={1} gap={2}>
        <Box p={1}>
          <HeadingWithDescription
            heading="Registered Appliances"
            description={`You have ${machines.length} registered appliance${
              machines?.length > 1 ? 's' : ''
            }`}
          />
        </Box>
        <Stack gap={2}>
          {machines.map((machinesDetail) => {
            const isAmc = machinesDetail?.machine_status.toLowerCase().trim() === 'amc';
            const daysDiff = moment(machinesDetail?.warranty_edate).diff(new Date(), 'd');
            const showBuyAMC =
              false && (machinesDetail.machine_code === 'WM' ? daysDiff < 365 : daysDiff < 30);
            return (
              <Card
                elevation={2}
                key={machinesDetail.zzr3ser_no}
                className="cursor-pointer"
                sx={{ borderRadius: 2 }}
              >
                <Box>
                  <Stack gap={1} direction={'row'} alignItems={'center'}>
                    <Box width={110} height={110}>
                      <img
                        alt={machinesDetail?.machine_type}
                        src={
                          machinesDetail?.prd_image_url ??
                          getProductImageByName(machinesDetail?.machine_type)
                        }
                        className="w-full h-full object-contain"
                      />
                    </Box>
                    <Stack p={1} gap={1} flex={1}>
                      <Stack gap={0.1}>
                        <Typography variant="body1" fontWeight={'500'}>
                          {machinesDetail?.machine_code} / IFB {machinesDetail?.machine_name}
                        </Typography>
                        {machinesDetail?.machine_status !== '' && (
                          <Typography
                            p={0.5}
                            borderRadius={1}
                            lineHeight={1}
                            variant="caption"
                            alignSelf={'flex-start'}
                            bgcolor={
                              machinesDetail?.machine_status === 'Out of Warranty'
                                ? theme.palette.error.main
                                : machinesDetail?.machine_status === ''
                                ? theme.palette.warning.main
                                : theme.palette.success.main
                            }
                            color="white"
                          >
                            {machinesDetail?.machine_status}
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction={'row'} gap={2}>
                        <TableContainer>
                          <Table size="small">
                            <TableBody>
                              <SingleRow
                                label={'Purchase Date'}
                                value={getFormattedDate(machinesDetail?.zzpurchase_date)}
                              />
                              {isAmc && (
                                <SingleRow
                                  label={(isAmc ? 'AMC ' : '') + 'Start Date'}
                                  value={getFormattedDate(machinesDetail?.warranty_sdate)}
                                />
                              )}

                              {machinesDetail?.machine_status !== OUT_OF_WARRANTY && (
                                <SingleRow
                                  label={(isAmc ? 'AMC ' : '') + 'Expiry Date'}
                                  value={getFormattedDate(machinesDetail?.warranty_edate)}
                                />
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Stack>

                      <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                        <Button
                          onClick={() =>
                            navigate('/raise-request?zzr3mat_id=' + machinesDetail?.zzr3mat_id)
                          }
                          variant="outlined"
                          size="small"
                          color="warning"
                        >
                          Register Complaint
                        </Button>
                        {showBuyAMC && (
                          <Button variant="contained" size="small" color="primary">
                            BUY AMC
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </>
  );
}
